import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Employee,
  HrisEmployee,
  PathEvaluator,
  AttributeConfig,
  AttributeConfigData,
  AttributeDefinition,
} from "winslow-core";

interface EmployeeCacheContextType {
  employees: Employee[];
  setEmployees: (employees: Employee[]) => void;
  isLoaded: boolean;
  setIsLoaded: (loaded: boolean) => void;
}

export const EmployeeCacheContext =
  createContext<EmployeeCacheContextType | null>(null);

export const useEmployeeCache = () => {
  const context = useContext(EmployeeCacheContext);
  if (!context) {
    throw new Error(
      "useEmployeeCache must be used within an EmployeeCacheProvider"
    );
  }
  return context;
};

export const useEmployeeCacheState = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  return {
    employees,
    setEmployees,
    isLoaded,
    setIsLoaded,
  };
};

const getHrisEmployees = (employees: Employee[]): HrisEmployee[] => {
  return employees
    .map((employee) => employee.data)
    .filter((data): data is HrisEmployee => data !== null);
};

export const useAttributeSuggestions = () => {
  const { employees } = useEmployeeCache();

  // Get predefined attribute configuration
  const attributeConfig = useMemo(() => {
    return AttributeConfig.getAvailableAttributes();
  }, []);

  // Check if a value is human-readable (not a UUID, long ID, etc.)
  const isHumanReadableValue = useCallback((value: string): boolean => {
    // Filter out UUIDs, technical IDs, and other non-human-readable values
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const technicalIdRegex = /^[a-zA-Z0-9_-]{20,}$/; // Long alphanumeric strings
    const numericOnlyRegex = /^\d+$/; // Pure numbers

    if (uuidRegex.test(value)) return false;
    if (technicalIdRegex.test(value)) return false;
    if (numericOnlyRegex.test(value) && value.length > 10) return false; // Long numeric IDs

    return true;
  }, []);

  // Filter and normalize values for suggestions
  const normalizeValueForSuggestion = useCallback(
    (value: any): string | null => {
      if (value === null || value === undefined) {
        return null;
      }

      let stringValue: string;

      if (typeof value === "string") {
        stringValue = value.trim();
      } else if (typeof value === "object" && value !== null) {
        // For objects, try to get meaningful string representation
        stringValue = (
          value.name ||
          value.id ||
          value.title ||
          JSON.stringify(value)
        ).trim();
      } else {
        stringValue = String(value).trim();
      }

      // Only return human-readable values
      if (stringValue && isHumanReadableValue(stringValue)) {
        return stringValue;
      }

      return null;
    },
    [isHumanReadableValue]
  );

  /**
   * Get suggestions for a specific attribute path using PathEvaluator
   */
  const getSuggestions = useCallback(
    (attributePath: string, searchTerm: string = ""): string[] => {
      if (!employees || employees.length === 0 || !attributePath) {
        return [];
      }

      const hrisEmployees = getHrisEmployees(employees);

      try {
        // Use PathEvaluator to extract all unique values for this path
        const allValues = PathEvaluator.extractAllValues(
          hrisEmployees,
          attributePath
        );

        // Normalize and filter values
        const suggestions = allValues
          .map((value) => normalizeValueForSuggestion(value))
          .filter((value): value is string => value !== null)
          .filter((value) => value.length > 0);

        // Filter by search term if provided
        let filteredSuggestions = suggestions;
        if (searchTerm.trim()) {
          const searchLower = searchTerm.toLowerCase();
          filteredSuggestions = suggestions.filter((value) =>
            value.toLowerCase().includes(searchLower)
          );
        }

        // Sort alphabetically and limit results
        filteredSuggestions.sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        );

        return filteredSuggestions.slice(0, 50); // Limit to 50 suggestions
      } catch (error) {
        console.warn(`Error getting suggestions for ${attributePath}:`, error);
        return [];
      }
    },
    [employees, normalizeValueForSuggestion]
  );

  /**
   * Check if an attribute path has any meaningful values in the current employee set
   */
  const hasValueForPath = useCallback(
    (path: string): boolean => {
      if (!employees || employees.length === 0 || !path) {
        return false;
      }

      const hrisEmployees = getHrisEmployees(employees);

      return hrisEmployees.some((employee) => {
        const value = PathEvaluator.extractValue(employee, path);

        if (value === null || value === undefined) {
          return false;
        }

        // Check if the extracted value is meaningful
        if (Array.isArray(value)) {
          return value.some((item) => {
            const normalized = normalizeValueForSuggestion(item);
            return normalized !== null;
          });
        } else {
          const normalized = normalizeValueForSuggestion(value);
          return normalized !== null;
        }
      });
    },
    [employees, normalizeValueForSuggestion]
  );

  /**
   * Get all available attribute paths from the complete configuration
   */
  const getAvailableAttributePaths = useCallback((): string[] => {
    if (!attributeConfig) {
      return [];
    }

    const allPaths = new Set<string>();

    // Get all paths from the complete attribute configuration
    Object.values(attributeConfig).forEach((categoryAttributes) => {
      categoryAttributes.forEach((attr: AttributeDefinition) => {
        allPaths.add(attr.path);
      });
    });

    // Filter to only include paths that have actual values in the current employee set
    const availablePaths = Array.from(allPaths).filter((path) =>
      hasValueForPath(path)
    );

    return availablePaths.sort();
  }, [attributeConfig, hasValueForPath]);

  /**
   * Get attribute metadata for a specific path
   */
  const getAttributeMetadata = useCallback(
    (path: string) => {
      if (!attributeConfig) {
        return null;
      }

      // Search for the attribute definition across all categories
      for (const categoryAttributes of Object.values(attributeConfig)) {
        const attribute = categoryAttributes.find(
          (attr: AttributeDefinition) => attr.path === path
        );
        if (attribute) {
          return attribute;
        }
      }

      return null;
    },
    [attributeConfig]
  );

  /**
   * Get the complete attribute configuration
   */
  const getAttributeConfig = useCallback((): AttributeConfigData => {
    return attributeConfig;
  }, [attributeConfig]);

  /**
   * Validate if a path is accessible for the current employee set
   */
  const validatePath = useCallback(
    (path: string): boolean => {
      if (!employees || employees.length === 0 || !path) {
        return false;
      }

      // Use PathEvaluator to validate the path against employee structure
      return employees.some((employee) =>
        PathEvaluator.validatePath(employee, path)
      );
    },
    [employees]
  );

  /**
   * Get suggestions with metadata about the attribute
   */
  const getSuggestionsWithMetadata = useCallback(
    (attributePath: string, searchTerm: string = "") => {
      const suggestions = getSuggestions(attributePath, searchTerm);
      const metadata = getAttributeMetadata(attributePath);

      return {
        suggestions,
        metadata,
        hasValues: suggestions.length > 0,
        isValid: validatePath(attributePath),
      };
    },
    [getSuggestions, getAttributeMetadata, validatePath]
  );

  return {
    getSuggestions,
    getAvailableAttributePaths,
    getAttributeMetadata,
    getAttributeConfig,
    hasValueForPath,
    validatePath,
    getSuggestionsWithMetadata,
  };
};
